import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { isTechAdmin } from "../../utils/permissions";
import {
  getPinnedChallengeUsers,
  PINNED_CHALLENGE_USERS_CHANGED_EVENT,
  savePinnedChallengeUsers,
} from "../../utils/pinnedChallengeUsers";
import { loadPinnedChallengeBets } from "../../utils/pinnedChallengeBets";

const REFRESH_INTERVAL = 8000;

const displayBetDate = (value) =>
  value ? moment(`${value}Z`).utc().local().format("DD/MM/YYYY HH:mm:ss") : "-";

export default function Challenge() {
  const userData = useSelector((state) => state.account.userData);
  const techAdmin = isTechAdmin(userData);
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [pinnedUsers, setPinnedUsers] = useState(() => getPinnedChallengeUsers());
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const requestId = useRef(0);

  const loadBets = useCallback(async () => {
    const currentRequest = ++requestId.current;

    if (!pinnedUsers.length) {
      setBets([]);
      setLoadError("");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");
    try {
      const currentBets = await loadPinnedChallengeBets(pinnedUsers);
      if (currentRequest === requestId.current) setBets(currentBets);
    } catch {
      if (currentRequest === requestId.current) {
        setBets([]);
        setLoadError("Unable to load current bets.");
      }
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [pinnedUsers]);

  useEffect(() => {
    loadBets();
    const refresh = window.setInterval(loadBets, REFRESH_INTERVAL);
    return () => window.clearInterval(refresh);
  }, [loadBets]);

  useEffect(() => {
    const syncPinnedUsers = () => setPinnedUsers(getPinnedChallengeUsers());
    window.addEventListener("storage", syncPinnedUsers);
    window.addEventListener(PINNED_CHALLENGE_USERS_CHANGED_EVENT, syncPinnedUsers);
    return () => {
      window.removeEventListener("storage", syncPinnedUsers);
      window.removeEventListener(PINNED_CHALLENGE_USERS_CHANGED_EVENT, syncPinnedUsers);
    };
  }, []);

  const totalAmount = useMemo(
    () => bets.reduce((total, bet) => total + Number(bet?.amount || 0), 0),
    [bets]
  );

  const addPinnedUser = (event) => {
    event.preventDefault();
    const trimmedUsername = username.trim();
    if (!trimmedUsername) {
      setUsernameError("User name is required.");
      return;
    }
    if (
      pinnedUsers.some(
        (pinnedUsername) => pinnedUsername.toLowerCase() === trimmedUsername.toLowerCase()
      )
    ) {
      setUsernameError("This user is already pinned.");
      return;
    }

    setPinnedUsers(savePinnedChallengeUsers([...pinnedUsers, trimmedUsername]));
    setUsername("");
    setUsernameError("");
  };

  const unpinUser = (usernameToRemove) => {
    setPinnedUsers(
      savePinnedChallengeUsers(
        pinnedUsers.filter(
          (pinnedUsername) =>
            pinnedUsername.toLowerCase() !== usernameToRemove.toLowerCase()
        )
      )
    );
  };

  if (!techAdmin) return null;

  return (
    <main className="main current-bets-page">
      <div className="container-fluid">
        <div className="main-container">
          <section className="listing-grid">
            <div className="detail-row">
              <h2>Challenges</h2>
            </div>

            <form onSubmit={addPinnedUser} noValidate className="row align-items-end">
              <div className="col-md-4">
                <label htmlFor="challenge-username">User Name</label>
                <input
                  id="challenge-username"
                  className="form-control"
                  name="username"
                  value={username}
                  onChange={(event) => {
                    setUsername(event.target.value);
                    setUsernameError("");
                  }}
                  autoComplete="off"
                  placeholder="Enter user name"
                />
                {usernameError && <small role="alert">{usernameError}</small>}
              </div>
              <div className="col-md-2">
                <button type="submit" className="btn btn-dark">
                  Pin User
                </button>
              </div>
            </form>

            <div className="current-bets-panel mt-3">
              <div className="current-bets-filter-row">
                <div>
                  <strong>Pinned challengers</strong>
                  {pinnedUsers.length ? (
                    <div className="mt-2">
                      {pinnedUsers.map((pinnedUsername) => (
                        <button
                          key={pinnedUsername.toLowerCase()}
                          type="button"
                          className="btn btn-light mr-2 mb-2"
                          onClick={() => unpinUser(pinnedUsername)}
                          aria-label={`Unpin ${pinnedUsername}`}
                        >
                          {pinnedUsername} ×
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="mb-0 mt-2">No pinned challengers.</p>
                  )}
                </div>
                <div className="current-bets-totals">
                  <span>Total Bets: {bets.length}</span>
                  <span>Total Amount: {totalAmount.toFixed(2)}</span>
                  <button
                    type="button"
                    className="btn btn-diamond current-bets-load"
                    disabled={loading || !pinnedUsers.length}
                    onClick={loadBets}
                  >
                    {loading ? "Loading..." : "Refresh"}
                  </button>
                </div>
              </div>

              <div className="table-responsive data-table">
                <table className="table table-bordered datatable dataTable current-bets-table">
                  <thead>
                    <tr>
                      <th>Event Type</th>
                      <th>Event Name</th>
                      <th>User Name</th>
                      <th>M Name</th>
                      <th>Nation</th>
                      <th className="current-bets-number">User Rate</th>
                      <th className="current-bets-number">Amount</th>
                      <th>Place Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!bets.length ? (
                      <tr>
                        <td colSpan="8" className="dataTables_empty">
                          {loading
                            ? "Loading bets..."
                            : loadError ||
                              (pinnedUsers.length
                                ? "No current bets for pinned challengers."
                                : "Pin a user to view current bets.")}
                        </td>
                      </tr>
                    ) : (
                      bets.map((bet, index) => (
                        <tr
                          key={bet.betId || bet.id || `${bet.created_date}-${index}`}
                          className={bet.betSide === "BACK" ? "back-bet-row" : "lay-bet-row"}
                        >
                          <td>{bet.event_type || "-"}</td>
                          <td>{bet.event_name || "-"}</td>
                          <td>{bet.userName || bet.username || "-"}</td>
                          <td>{bet.marketName || "-"}</td>
                          <td>{bet.runner_name || "-"}</td>
                          <td className="current-bets-number">{bet.userRate}</td>
                          <td className="current-bets-number">{bet.amount}</td>
                          <td>{displayBetDate(bet.created_date)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
